import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  getCollectionsSettings,
  updateCollectionsSettings,
  listEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
} from '../api';
import { useConfirm } from './ConfirmProvider';

/* ───────────────────────── constants ───────────────────────── */
const STEPS = [
  { key: 'settings', label: 'Settings', icon: '⚙️' },
  { key: 'schedule', label: 'Schedule', icon: '📅' },
  { key: 'templates', label: 'Templates', icon: '✉️' },
];

/* ───────────────── debounce helper (template saves) ────────── */
function useDebouncedCallback(fn, delay = 800) {
  const timer = useRef(null);
  const latest = useRef(fn);
  latest.current = fn;
  return useCallback((...args) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => latest.current(...args), delay);
  }, [delay]);
}

/* ═══════════════════════ MAIN COMPONENT ════════════════════ */
const CollectionsSettings = ({ authAxios, permissions }) => {
  const confirm = useConfirm();
  /* ── shared state ── */
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [step, setStep] = useState(0);

  /* ── settings state ── */
  const [settings, setSettings] = useState({
    collections_enabled: false,
    collections_grace_days: 7,
    collections_schedule: [0, 3, 7],
    max_collections_stages: 3,
    collections_min_interval_hours: 12,
  });

  /* ── template state ── */
  const [templates, setTemplates] = useState([]);
  const [editDrafts, setEditDrafts] = useState({}); // { [tplId]: { ...patchedFields } }
  const [newTemplate, setNewTemplate] = useState({ stage: 1, name: '', subject_template: '', body_template: '' });

  // Server-side: PARTNER, ACCOUNTANT, or SUPERUSER may edit collections settings.
  const canEdit = useMemo(
    () => (permissions?.isPartner || permissions?.isAccountant || permissions?.isSuperuser),
    [permissions],
  );

  /* ── schedule chips helper ── */
  const [scheduleInput, setScheduleInput] = useState('');

  /* ───── Load data ───── */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [s, t] = await Promise.all([
        getCollectionsSettings(authAxios),
        listEmailTemplates(authAxios, { category: 'collections' }),
      ]);
      setSettings(s);
      setScheduleInput((s.collections_schedule || []).join(', '));
      const tplArr = Array.isArray(t) ? t : (t?.results || []);
      setTemplates(tplArr);
    } catch (e) {
      console.error('Failed to load collections config', e);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { loadData(); }, [loadData]);

  /* ───── Save settings (steps 1 + 2) ───── */
  const handleSaveSettings = async () => {
    if (!canEdit) return;
    try {
      setSaving(true);
      setSaveMsg('');
      const payload = { ...settings };
      payload.collections_schedule = scheduleInput
        .split(',')
        .map(s => parseInt(s.trim(), 10))
        .filter(x => !isNaN(x));
      const resp = await updateCollectionsSettings(authAxios, payload);
      setSettings(resp);
      setScheduleInput((resp.collections_schedule || []).join(', '));
      setSaveMsg('Settings saved.');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (e) {
      console.error('Failed to save settings', e);
      setSaveMsg('Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  /* ───── Template CRUD (debounced inline save) ───── */
  const persistTemplatePatch = useCallback(async (tplId, patch) => {
    if (!canEdit) return;
    try {
      const updated = await updateEmailTemplate(authAxios, tplId, patch);
      setTemplates(prev => prev.map(t => (t.id === tplId ? updated : t)));
      // clear draft for this template since it's now persisted
      setEditDrafts(prev => { const n = { ...prev }; delete n[tplId]; return n; });
    } catch (e) {
      console.error('Failed to update template', e);
    }
  }, [authAxios, canEdit]);

  const debouncedPersist = useDebouncedCallback((tplId, accPatch) => {
    persistTemplatePatch(tplId, accPatch);
  }, 1000);

  const handleTemplateField = (tpl, field, value) => {
    setEditDrafts(prev => {
      const existing = prev[tpl.id] || {};
      const updated = { ...existing, [field]: value };
      // Fire debounced persist with accumulated patch
      debouncedPersist(tpl.id, updated);
      return { ...prev, [tpl.id]: updated };
    });
  };

  const getDraftValue = (tpl, field) => {
    return editDrafts[tpl.id]?.[field] ?? tpl[field] ?? '';
  };

  const handleCreateTemplate = async () => {
    if (!canEdit) return;
    try {
      const data = {
        category: 'collections',
        stage: parseInt(newTemplate.stage, 10) || 1,
        name: newTemplate.name || `Collections Stage ${newTemplate.stage}`,
        subject_template: newTemplate.subject_template,
        body_template: newTemplate.body_template,
        is_active: true,
      };
      const created = await createEmailTemplate(authAxios, data);
      setTemplates(prev => [...prev, created]);
      setNewTemplate({ stage: 1, name: '', subject_template: '', body_template: '' });
    } catch (e) {
      console.error('Failed to create template', e);
    }
  };

  const handleDeleteTemplate = async (tpl) => {
    if (!canEdit || !(await confirm({ title: 'Delete Template', message: `Delete template "${tpl.name || 'Untitled'}"?`, confirmText: 'Delete', variant: 'danger' }))) return;
    try {
      await deleteEmailTemplate(authAxios, tpl.id);
      setTemplates(prev => prev.filter(t => t.id !== tpl.id));
    } catch (e) {
      console.error('Failed to delete template', e);
    }
  };

  /* ───── Navigation ───── */
  const goNext = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setStep(s => Math.max(s - 1, 0));

  /* ───── Render ───── */
  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span className="ml-3 text-gray-600">Loading collections settings…</span>
      </div>
    );
  }

  const collectionsTpls = templates
    .filter(t => t.category === 'collections')
    .sort((a, b) => (a.stage || 0) - (b.stage || 0));

  return (
    <div className="p-4 max-w-4xl mx-auto space-y-6">
      {/* ── Stepper header ── */}
      <nav className="flex items-center justify-between bg-white rounded-lg shadow px-4 py-3">
        {STEPS.map((s, i) => {
          const isActive = i === step;
          const isDone = i < step;
          return (
            <button
              key={s.key}
              onClick={() => setStep(i)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors
                ${isActive ? 'bg-blue-600 text-white shadow' : isDone ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
            >
              <span className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold border-2
                ${isActive ? 'border-white bg-white text-blue-700' : isDone ? 'border-blue-400 bg-blue-100 text-blue-700' : 'border-gray-300 bg-gray-100 text-gray-500'}`}>
                {isDone ? '✓' : i + 1}
              </span>
              <span className="hidden sm:inline">{s.icon} {s.label}</span>
            </button>
          );
        })}
      </nav>

      {/* ── Success message ── */}
      {saveMsg && (
        <div className={`px-4 py-2 rounded text-sm font-medium ${saveMsg.includes('Failed') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {saveMsg}
        </div>
      )}

      {/* ═══════════ STEP 1: Settings ═══════════ */}
      {step === 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 space-y-6 animate__animated animate__fadeIn">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">General Settings</h3>
            <p className="text-sm text-gray-500 mt-1">Control whether automated debt-collection dunning is active.</p>
          </div>

          <label className="flex items-center gap-3 text-sm">
            <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.collections_enabled ? 'bg-blue-600' : 'bg-gray-300'}`}>
              <input
                type="checkbox"
                className="sr-only"
                checked={settings.collections_enabled}
                disabled={!canEdit}
                onChange={e => setSettings({ ...settings, collections_enabled: e.target.checked })}
              />
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.collections_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </div>
            <span className="font-medium">{settings.collections_enabled ? 'Collections Enabled' : 'Collections Disabled'}</span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Grace Period (days)</label>
              <input
                type="number"
                min="0"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                value={settings.collections_grace_days}
                disabled={!canEdit}
                onChange={e => setSettings({ ...settings, collections_grace_days: parseInt(e.target.value || 0, 10) })}
              />
              <p className="text-xs text-gray-500 mt-1">Days after invoice due-date before the first dunning action fires.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Interval Between Emails (hours)</label>
              <input
                type="number"
                min="1"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                value={settings.collections_min_interval_hours}
                disabled={!canEdit}
                onChange={e => setSettings({ ...settings, collections_min_interval_hours: parseInt(e.target.value || 12, 10) })}
              />
              <p className="text-xs text-gray-500 mt-1">Rate limit between successive dunning emails (prevents spam).</p>
            </div>
          </div>

          <div className="flex justify-between pt-4 border-t border-gray-200">
            <div />
            <div className="flex gap-3">
              {canEdit && (
                <button
                  className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
                  disabled={saving}
                  onClick={async () => { await handleSaveSettings(); }}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              )}
              <button className="bg-gray-800 text-white px-4 py-2 rounded-md text-sm hover:bg-gray-900" onClick={goNext}>
                Next: Schedule →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ STEP 2: Schedule ═══════════ */}
      {step === 1 && (
        <div className="bg-white rounded-lg shadow-md p-6 space-y-6 animate__animated animate__fadeIn">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Dunning Schedule</h3>
            <p className="text-sm text-gray-500 mt-1">Define the escalation stages and their day-offsets after the grace period.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Stages</label>
              <input
                type="number"
                min="1"
                max="10"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                value={settings.max_collections_stages}
                disabled={!canEdit}
                onChange={e => setSettings({ ...settings, max_collections_stages: parseInt(e.target.value || 1, 10) })}
              />
              <p className="text-xs text-gray-500 mt-1">Maximum escalation depth. Match this with your email templates.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Day-Offset Schedule</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="0, 3, 7"
                value={scheduleInput}
                disabled={!canEdit}
                onChange={e => setScheduleInput(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Comma-separated days after grace period. E.g. <code className="bg-gray-100 px-1 rounded">0, 3, 7</code> means
                emails at day 0, 3, and 7 post-grace.
              </p>
            </div>
          </div>

          {/* Visual timeline */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Preview timeline</h4>
            <div className="relative flex items-center gap-1 overflow-x-auto pb-2">
              {/* Grace period bar */}
              <div className="flex-shrink-0 bg-amber-100 border border-amber-300 rounded-lg px-3 py-2 text-xs text-amber-800 font-medium text-center">
                Invoice due date<br />+ {settings.collections_grace_days} day grace
              </div>
              <div className="flex-shrink-0 text-gray-400">→</div>
              {scheduleInput.split(',').map((raw, i) => {
                const d = parseInt(raw.trim(), 10);
                if (isNaN(d)) return null;
                const totalDay = settings.collections_grace_days + d;
                return (
                  <React.Fragment key={i}>
                    <div className="flex-shrink-0 bg-blue-50 border border-blue-300 rounded-lg px-3 py-2 text-xs text-blue-800 font-medium text-center">
                      Stage {i + 1}<br />Day {totalDay}
                    </div>
                    {i < scheduleInput.split(',').filter(s => !isNaN(parseInt(s.trim(), 10))).length - 1 && (
                      <div className="flex-shrink-0 text-gray-400">→</div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <div className="flex justify-between pt-4 border-t border-gray-200">
            <button className="text-gray-600 px-4 py-2 rounded-md text-sm hover:bg-gray-100" onClick={goBack}>
              ← Back
            </button>
            <div className="flex gap-3">
              {canEdit && (
                <button
                  className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
                  disabled={saving}
                  onClick={async () => { await handleSaveSettings(); }}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              )}
              <button className="bg-gray-800 text-white px-4 py-2 rounded-md text-sm hover:bg-gray-900" onClick={goNext}>
                Next: Templates →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ STEP 3: Templates ═══════════ */}
      {step === 2 && (
        <div className="bg-white rounded-lg shadow-md p-6 space-y-6 animate__animated animate__fadeIn">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Email Templates</h3>
            <p className="text-sm text-gray-500 mt-1">
              Configure dunning email templates per stage. Changes auto-save after you stop typing.
            </p>
          </div>

          {collectionsTpls.length === 0 ? (
            <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded border">
              No templates yet. Add one below to get started.
            </div>
          ) : (
            <div className="space-y-4">
              {collectionsTpls.map((tpl) => (
                <div key={tpl.id} className="border border-gray-200 rounded-lg p-4 space-y-3 hover:shadow transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 text-sm font-bold">
                        {tpl.stage || '?'}
                      </span>
                      <input
                        type="text"
                        className="font-medium text-gray-800 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none px-1 py-0.5 text-sm"
                        value={getDraftValue(tpl, 'name')}
                        disabled={!canEdit}
                        onChange={e => handleTemplateField(tpl, 'name', e.target.value)}
                        placeholder="Template name"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={editDrafts[tpl.id]?.is_active ?? tpl.is_active ?? true}
                          disabled={!canEdit}
                          onChange={e => {
                            // Checkbox: persist immediately, no debounce
                            persistTemplatePatch(tpl.id, { is_active: e.target.checked });
                          }}
                        />
                        Active
                      </label>
                      {canEdit && (
                        <button
                          className="text-red-500 hover:text-red-700 text-xs font-medium"
                          onClick={() => handleDeleteTemplate(tpl)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Subject</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-blue-500 focus:border-blue-500"
                      value={getDraftValue(tpl, 'subject_template')}
                      disabled={!canEdit}
                      onChange={e => handleTemplateField(tpl, 'subject_template', e.target.value)}
                      placeholder="Email subject…"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Body</label>
                    <textarea
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                      rows={4}
                      value={getDraftValue(tpl, 'body_template')}
                      disabled={!canEdit}
                      onChange={e => handleTemplateField(tpl, 'body_template', e.target.value)}
                      placeholder="Dear {{ client_name }},…"
                    />
                  </div>

                  <div className="text-xs text-gray-400">
                    Available variables: {'{{ client_name }}'}, {'{{ invoice_number }}'}, {'{{ amount_due }}'}, {'{{ due_date }}'}, {'{{ firm_name }}'}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add new template */}
          {canEdit && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-semibold text-gray-700">+ Add New Template</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Stage</label>
                  <input
                    type="number"
                    min="1"
                    className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={newTemplate.stage}
                    onChange={e => setNewTemplate({ ...newTemplate, stage: parseInt(e.target.value || 1, 10) })}
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs text-gray-500 mb-1">Name</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={newTemplate.name}
                    placeholder={`Collections Stage ${newTemplate.stage}`}
                    onChange={e => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Subject</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={newTemplate.subject_template}
                    onChange={e => setNewTemplate({ ...newTemplate, subject_template: e.target.value })}
                  />
                </div>
                <div className="md:col-span-4">
                  <label className="block text-xs text-gray-500 mb-1">Body</label>
                  <textarea
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    rows={4}
                    value={newTemplate.body_template}
                    onChange={e => setNewTemplate({ ...newTemplate, body_template: e.target.value })}
                  />
                </div>
              </div>
              <button
                className="bg-green-600 text-white px-4 py-2 rounded-md text-sm hover:bg-green-700"
                onClick={handleCreateTemplate}
              >
                Create Template
              </button>
            </div>
          )}

          <div className="flex justify-between pt-4 border-t border-gray-200">
            <button className="text-gray-600 px-4 py-2 rounded-md text-sm hover:bg-gray-100" onClick={goBack}>
              ← Back
            </button>
            <div className="text-sm text-gray-500 self-center">Templates auto-save as you type</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionsSettings;
